import { VerticalTimelineElement } from 'react-vertical-timeline-component';

import useTooltipActions from '@/apps/mwd-v2/state/actions/useTooltipActions';
import { getConflictIcon } from '@/components/detailPanel/timeLine/getIcons/GetConflictIcon';
import { getRushIcon } from '@/components/detailPanel/timeLine/getIcons/GetRushIcon';
import { gekoppeldeInzet, Station } from '@/components/detailPanel/types/types';
import { getTimelineClassName } from '@/components/detailPanel/utils/utils';

import 'react-vertical-timeline-component/style.min.css';
import styles from '../DetailPanel.module.css';

export function TimelineStationElement({
  inzetId,
  station,
  color,
  iconBorderColor,
  showAllStops = true,
  zitPlaatsen,
  comfortabelNorm,
  acceptabelNorm,
  volNorm,
  gekoppeldeInzetten,
  showDetails = true,
  inzetIsManco,
}: Readonly<{
  inzetId?: string;
  station: Station;
  color: string;
  iconBorderColor: string;
  showAllStops?: boolean;
  zitPlaatsen?: number;
  comfortabelNorm?: number;
  acceptabelNorm?: number;
  volNorm?: number;
  gekoppeldeInzetten?: gekoppeldeInzet[];
  showDetails?: boolean;
  inzetIsManco?: boolean;
}>) {
  const {
    setShowDetailPanelOnSpoorTooltip,
    setShowDetailPanelOnTimesTooltip,
    hideTooltipWithTimeout,
    setShowDetailPanelOnPrognoseTooltip,
  } = useTooltipActions();

  const handleMouseMoveOnTime = (event: React.MouseEvent) => {
    if (station.arrivalDelayed || station.arrivalIsEarly || station.departureDelayed || station.departureIsEarly) {
      void setShowDetailPanelOnTimesTooltip({ x: event.clientX, y: event.clientY }, station);
    }
  };

  const handleMouseMoveOnSpoor = (event: React.MouseEvent) => {
    if (station.actueelSpoor !== station.geplandSpoor) {
      void setShowDetailPanelOnSpoorTooltip({ x: event.clientX, y: event.clientY }, station);
    }
  };

  const handleMouseMoveOnPrognose = (event: React.MouseEvent) => {
    if (station.prognose && station.prognose > 0) {
      void setShowDetailPanelOnPrognoseTooltip(
        { x: event.clientX, y: event.clientY },
        { station, zitPlaatsen, comfortabelNorm, acceptabelNorm, volNorm, gekoppeldeInzetten, showAllStops },
      );
    }
  };

  const handleMouseLeave = () => {
    void hideTooltipWithTimeout();
  };

  const getTimeColor = (isDelayed?: boolean, isEarly?: boolean) => {
    if (isDelayed) return 'var(--tractie-color-content-error)';
    if (isEarly) return 'var(--tractie-color-content-success)';
    return '';
  };

  const spoorColor =
    station.actueelSpoor !== station.geplandSpoor
      ? 'var(--tractie-color-content-error)'
      : 'var(--tractie-color-content-subtle)';
  const shouldShowPrognose = showDetails && !!station.prognose && station.prognose > 0;
  const usedPrognose = showAllStops ? station.prognose : station.highestPrognose;
  const displayPrognose =
    !showAllStops && station.lowestPrognose !== station.highestPrognose
      ? `${station.highestPrognose}\u200A•`
      : station.prognose;
  const normComfortabel = inzetIsManco ? 0 : comfortabelNorm;
  const normAcceptable = inzetIsManco ? 0 : acceptabelNorm;
  const rushIcon = getRushIcon(usedPrognose, normComfortabel, normAcceptable, gekoppeldeInzetten);
  const arrivalColor = getTimeColor(station.arrivalDelayed, station.arrivalIsEarly);
  const departureColor = getTimeColor(station.departureDelayed, station.departureIsEarly);

  return (
    <VerticalTimelineElement
      className={getTimelineClassName(showAllStops, styles.timeLineElement, station.isKnoopStationWithKnoopStopBefore)}
      style={{ maxHeight: showDetails ? 61 : 40 }}
      iconStyle={{
        zIndex: 1,
        background: 'var(--tractie-color-background-default)',
        border: `2px solid ${iconBorderColor}`,
        width: '16px',
        height: '16px',
        boxShadow: 'none',
        top: '11px',
        left: '11px',
        display: station.materieelKnoop ? 'block' : 'none',
      }}
      contentStyle={{
        boxShadow: 'none',
        padding: '8px 0px',
        background: 'var(--tractie-color-background-default)',
        borderBottom: !showDetails || !station.departure ? 'none' : '1px solid var(--tractie-color-border-default)',
        marginLeft: '40px',
        marginRight: '6px',
        borderRadius: 0,
        maxHeight: showDetails ? 61 : 40,
      }}
      contentArrowStyle={{
        borderRight: 'none',
        width: '6px',
        height: '2px',
        marginRight: '14px',
        top: '19px',
        background: color,
        border: 'none',
        display: station.materieelKnoop ? 'none' : 'block',
      }}
    >
      <div
        style={{
          fontSize: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 4,
          color: 'var(--tractie-color-content-emphasis)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div
              data-testid={`${inzetId}-station-${station.name}`}
              style={{
                marginBottom: 8,
                width: '45px',
              }}
            >
              {station.name}
            </div>
            <div
              onMouseMove={handleMouseMoveOnSpoor}
              onMouseLeave={handleMouseLeave}
              data-testid={`${inzetId}-spoor-${station.name}`}
              style={{
                textTransform: 'lowercase',
                marginBottom: 8,
                fontSize: '16px',
                color: spoorColor,
              }}
            >
              {station.actueelSpoor}
            </div>

            <div style={{ fontSize: '10px' }}>{getConflictIcon(station?.conflicts)}</div>
          </div>

          {shouldShowPrognose ? (
            <div style={{ display: 'flex', justifyContent: 'normal', gap: 24 }}>
              <div
                onMouseMove={handleMouseMoveOnPrognose}
                onMouseLeave={handleMouseLeave}
                data-testid={`${inzetId}-prognose-${station.name}`}
                style={{
                  display: 'flex',
                  marginTop: -6,
                  fontSize: '14px',
                  height: '25px',
                  color: 'var(--tractie-color-content-subtle)',
                  flexDirection: 'row',
                  gap: 4,
                  alignItems: 'center',
                }}
              >
                {displayPrognose}
                {rushIcon}
              </div>
            </div>
          ) : null}
        </div>
        <div onMouseMove={handleMouseMoveOnTime} onMouseLeave={handleMouseLeave}>
          <span
            data-testid={`${inzetId}-arrival-${station.name}`}
            style={{
              color: arrivalColor,
            }}
          >
            {!station.departure && 'A'} {station.arrival}
          </span>
          {station.arrival && station.departure && ' - '}
          <span
            data-testid={`${inzetId}-departure-${station.name}`}
            style={{
              color: departureColor,
            }}
          >
            {!station.arrival && 'V'} {station.departure}
          </span>
        </div>
      </div>
    </VerticalTimelineElement>
  );
}
